import { subDays, subWeeks, startOfDay, endOfDay } from 'date-fns';
import type { DateRange } from 'react-day-picker';

export type PresetPeriod =
  | 'all_time'
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export interface PresetOption {
  value: PresetPeriod;
  label: string;
  getRange: () => DateRange | undefined;
}

export const presetOptions: PresetOption[] = [
  { value: 'all_time', label: 'All Time', getRange: () => undefined },
  {
    value: 'today',
    label: 'Today',
    getRange: () => ({
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    value: 'yesterday',
    label: 'Yesterday',
    getRange: () => ({
      from: startOfDay(subDays(new Date(), 1)),
      to: endOfDay(subDays(new Date(), 1)),
    }),
  },
  {
    value: 'last_7_days',
    label: 'Last 7 days',
    getRange: () => ({
      from: startOfDay(subDays(new Date(), 6)),
      to: endOfDay(new Date()),
    }),
  },
  {
    value: 'last_30_days',
    label: 'Last 30 days',
    getRange: () => ({
      from: startOfDay(subDays(new Date(), 29)),
      to: endOfDay(new Date()),
    }),
  },
  {
    value: 'this_week',
    label: 'This week',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return { from: startOfDay(start), to: endOfDay(now) };
    },
  },
  {
    value: 'last_week',
    label: 'Last week',
    getRange: () => {
      const now = new Date();
      const start = subWeeks(now, 1);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { from: startOfDay(start), to: endOfDay(end) };
    },
  },
  {
    value: 'this_month',
    label: 'This month',
    getRange: () => {
      const now = new Date();
      return {
        from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: endOfDay(now),
      };
    },
  },
  {
    value: 'last_month',
    label: 'Last month',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(start), to: endOfDay(end) };
    },
  },
  {
    value: 'this_quarter',
    label: 'This quarter',
    getRange: () => {
      const now = new Date();
      const quarter = Math.floor(now.getMonth() / 3);
      return {
        from: startOfDay(new Date(now.getFullYear(), quarter * 3, 1)),
        to: endOfDay(now),
      };
    },
  },
  {
    value: 'last_quarter',
    label: 'Last quarter',
    getRange: () => {
      const now = new Date();
      const quarter = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), (quarter - 1) * 3, 1);
      const end = new Date(now.getFullYear(), quarter * 3, 0);
      return { from: startOfDay(start), to: endOfDay(end) };
    },
  },
  {
    value: 'this_year',
    label: 'This year',
    getRange: () => {
      const now = new Date();
      return {
        from: startOfDay(new Date(now.getFullYear(), 0, 1)),
        to: endOfDay(now),
      };
    },
  },
  {
    value: 'last_year',
    label: 'Last year',
    getRange: () => {
      const now = new Date();
      return {
        from: startOfDay(new Date(now.getFullYear() - 1, 0, 1)),
        to: endOfDay(new Date(now.getFullYear() - 1, 11, 31)),
      };
    },
  },
];
